/**
 * Refresh policy for the credit summary (`user/v1/creditSummary/{_Id}`, `GET_SUBSCRIPTION_HISTORY`)
 * that drives the global subscription banners in `MainLayout/SubscriptionBanners`.
 *
 * The banner is mounted once per session by `MainLayout`, so the summary should be fetched:
 * - once on mount,
 * - when a subscription-changing action completes (allocate / cancel / Stripe return / device add
 *   that starts a trial) — signalled with `dispatchSubscriptionSummaryRefresh()`,
 * - on window focus, but only when the last fetch is older than `FOCUS_REFRESH_MIN_INTERVAL_MS`
 *   (covers plan changes made in another tab without refetching on every alt-tab).
 *
 * It must NOT be refetched on every route change (PRJCBUGEN: creditSummary called on every page).
 */

/** window CustomEvent name: "subscription data changed, refresh the global banner summary". */
export const SUBSCRIPTION_SUMMARY_REFRESH_EVENT = 'subscriptionSummaryRefresh';

/** Minimum age of the cached summary before a window focus triggers a background refetch. */
export const FOCUS_REFRESH_MIN_INTERVAL_MS = 5 * 60 * 1000;

/**
 * Notify the global banner that subscription state changed and the credit summary must be refetched.
 * Safe to call in non-browser environments (no-op).
 */
export function dispatchSubscriptionSummaryRefresh() {
  if (typeof window === 'undefined' || typeof window.dispatchEvent !== 'function') return;
  if (typeof CustomEvent !== 'function') return;
  window.dispatchEvent(new CustomEvent(SUBSCRIPTION_SUMMARY_REFRESH_EVENT));
}

/**
 * Decide whether a window focus / tab-visible event should refetch the summary.
 *
 * @param {object} params
 * @param {number|null|undefined} params.lastFetchedAt - epoch ms of the last completed fetch, or null if never fetched
 * @param {number} params.now - current epoch ms
 * @param {boolean} [params.isVisible=true] - `document.visibilityState === 'visible'`
 * @param {number} [params.minIntervalMs=FOCUS_REFRESH_MIN_INTERVAL_MS]
 * @returns {boolean}
 */
export function shouldRefreshSummaryOnFocus({ lastFetchedAt, now, isVisible = true, minIntervalMs = FOCUS_REFRESH_MIN_INTERVAL_MS }) {
  if (!isVisible) return false;
  if (lastFetchedAt == null || !Number.isFinite(lastFetchedAt)) return true;
  return now - lastFetchedAt >= minIntervalMs;
}
